import React from "react";

import { CircleDollarSign, EyeOff, FileX2 } from "lucide-react";

import { MultiplePricingCardPartner, SoloPricingCardPartner } from "@/components/pricing/pricing-card-partner";
import { cn } from "@/lib/utils";

import type { LucideIcon } from "lucide-react";
import type { PricingCard } from "@/pages/partners/[partnerName]";

interface PricingBenefit {
  icon: LucideIcon;
  heading: string;
  description: string;
}

const PRICING_BENEFITS: PricingBenefit[] = [
  {
    icon: CircleDollarSign,
    heading: "No upfront cost.",
    description:
      "Launch your website without paying a dime upfront — only start your subscription once you are happy with your site.",
  },
  {
    icon: FileX2,
    heading: "No contracts.",
    description: "Stay flexible. Cancel anytime, no long-term commitments or hidden fine print.",
  },
  {
    icon: EyeOff,
    heading: "No hidden fees.",
    description: "What you see is what you pay. Transparent monthly pricing with everything included.",
  },
];

const Pricing = ({ pricingCards, ctaLink }: { pricingCards: PricingCard[]; ctaLink: string }) => {
  return (
    <div className="bg-black text-white px-5 md:px-16 md:py-28 py-16">
      <div
        className={cn(
          "flex flex-col justify-between lg:gap-8 gap-12 max-w-7xl mx-auto relative",
          pricingCards.length > 2 ? "flex-col" : "lg:flex-row"
        )}
      >
        <div
          className={cn(
            "flex flex-col gap-14 w-full",
            pricingCards.length === 1 ? "lg:w-1/2" : pricingCards.length === 2 ? "lg:w-1/3" : "w-full"
          )}
        >
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2 text-white font-semibold">
              <p>Plan</p>
              <h1 className="font-clash font-semibold text-4xl md:text-5xl leading-[1.2] tracking-tight">
                Pricing made simple
              </h1>
            </div>
          </div>
          <div
            className={cn(
              "flex gap-12 max-w-none",
              pricingCards.length > 2 ? "max-w-none lg:flex-row flex-col w-full" : "flex-col lg:max-w-md "
            )}
          >
            {PRICING_BENEFITS.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className={cn("flex gap-4 w-full", pricingCards.length > 2 ? "lg:w-1/3" : "")}>
                  <div className="shrink-0">
                    <Icon className="w-8 h-8 text-rebolt-500" />
                  </div>
                  <div className="flex flex-col gap-1 -mt-1">
                    <p className="tracking-tight leading-[1.4] font-semibold text-lg md:text-2xl">{benefit.heading}</p>
                    <p className="text-sm md:text-base">{benefit.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {pricingCards.length > 2 && <div className="w-full h-px bg-ash-700 my-5" />}
        <div className={cn("w-full h-full", pricingCards.length > 2 ? "lg:w-full" : "lg:w-2/3")}>
          <PricingSection pricingCards={pricingCards} ctaLink={ctaLink} />
        </div>
      </div>
    </div>
  );
};

export default Pricing;

export function PricingSection({ pricingCards, ctaLink }: { pricingCards: PricingCard[]; ctaLink: string }) {
  return (
    <div>
      {pricingCards.length === 1 ? (
        <SoloPricingCardPartner key={pricingCards[0].fields.header} pricingCard={pricingCards[0]} ctaLink={ctaLink} />
      ) : (
        <div className={cn("grid grid-cols-1 gap-4 w-full", getGridCols(pricingCards))}>
          {pricingCards.map((card) => (
            <MultiplePricingCardPartner key={card.fields.header} pricingCard={card} ctaLink={ctaLink} />
          ))}
        </div>
      )}
    </div>
  );
}

function getGridCols(pricingCards: PricingCard[]): string {
  if (pricingCards.length === 1) return "grid-cols-1";
  if (pricingCards.length === 2) return "grid-cols-1 lg:grid-cols-2";
  if (pricingCards.length === 3) return "grid-cols-1 lg:grid-cols-3";
  return "grid-cols-1";
}
